<template>
    <div class="forgot-password">
        <h1>Reset Your Password</h1>
        <div style="background: white">
            <Message v-if="errorMessage" :closable="false" severity="error">
                {{ errorMessage }}
            </Message>
            <Message v-if="successMessage" :closable="false" severity="success">
                {{ successMessage }}
            </Message>
        </div>
        <div class="reset-form">
            <p class="instructions">
                Enter your email address and we'll send you a link to reset your
                password.
            </p>
            <div class="input email">
                <label for="email">Email</label>
                <InputText
                    id="email"
                    v-model="email"
                    autocomplete="email"
                    :disabled="isSubmitting"
                    @keyup.enter="sendResetLink"
                />
            </div>
            <div class="buttons">
                <Button
                    class="p-button-raised button"
                    label="Send Reset Link"
                    :loading="isSubmitting"
                    @click="sendResetLink"
                />
                <Button
                    class="p-button-text back-button"
                    label="Back to Login"
                    @click="goToLogin"
                />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useAuthenticationStore } from "@/stores/authentication-store";
import { ref } from "vue";
import { useRouter } from "vue-router";

const router = useRouter();
const { sendPasswordResetEmail } = useAuthenticationStore();

const email = ref("");
const errorMessage = ref("");
const successMessage = ref("");
const isSubmitting = ref(false);

const sendResetLink = async () => {
    if (!email.value) {
        errorMessage.value = "Please enter your email address";
        return;
    }

    isSubmitting.value = true;
    errorMessage.value = "";
    successMessage.value = "";

    try {
        const success = await sendPasswordResetEmail(email.value);
        if (success) {
            successMessage.value =
                "Password reset link sent! Check your email.";
            email.value = "";
        }
    } catch (error) {
        if (error instanceof Error) {
            errorMessage.value = error.message;
        } else {
            errorMessage.value =
                "An error occurred while sending the reset link.";
        }
    } finally {
        isSubmitting.value = false;
    }
};

const goToLogin = () => {
    router.push("/login");
};
</script>

<style lang="scss" scoped>
.forgot-password {
    display: flex;
    flex: 1;
    flex-direction: column;
    justify-content: center;
    height: 100vh;
    padding: 30px;
    align-items: center;
    color: var(--stekz-white);
    background-color: var(--stekz-primary-color);

    h1 {
        color: var(--stekz-white);
        margin-bottom: 1.5rem;
    }

    > div {
        max-width: 320px;
    }
}

.reset-form {
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-top: 1rem;

    .instructions {
        margin-bottom: 1.5rem;
        text-align: center;
    }

    .input {
        margin-bottom: 1.5rem;

        label {
            display: block;
            margin-bottom: 0.5rem;
        }
    }
}

.buttons {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 0.5rem;
    margin-top: 0.5rem;
}

.button {
    background-color: var(--stekz-dev-color);
    color: var(--stekz-white);
    width: 100%;
}

.back-button {
    color: var(--stekz-white);
}

.p-inputtext {
    width: 100%;
}
</style>
